import { del, get, patch, post, put } from "./api";
import type {
  AdminLeasedNumbers,
  ApproveNumberRequestRequest,
  AvailableNumber,
  AvailableNumberSearch,
  BulkSendPreview,
  ClaimPhoneNumberRequest,
  CreateNumberRequestRequest,
  LogPersonalSendRequest,
  MessagingNumberQuota,
  MessagingNumberRequest,
  MergeField,
  MessageSender,
  MessagingContactSummary,
  MessagingContactType,
  MessagingConversation,
  MessagingMatchCandidate,
  MessagingMessage,
  MessagingNumberHolder,
  MessagingOptOut,
  MessagingPhoneNumber,
  MessagingTemplate,
  PreviewBulkSendRequest,
  SaveMessagingTemplateRequest,
  SendMessageRequest,
  SendMessageResponse,
} from "../types/messaging";
import type { PaginatedResponse } from "../types/api";

// --- Inbox ---

/**
 * The Messaging inbox. Messaging keeps its own threads and messages, so this is
 * the only list SMS ever appears in — there is no CRM inbox to reconcile with.
 * Pass `unmatchedOnly` for replies no player record claims yet.
 */
export const getMessagingConversations = (
  pageNumber = 1,
  pageSize = 20,
  unreadOnly = false,
  unmatchedOnly = false
): Promise<PaginatedResponse<MessagingConversation>> =>
  get(
    `/api/messaging/conversations?pageNumber=${pageNumber}&pageSize=${pageSize}` +
      `&unreadOnly=${unreadOnly}&unmatchedOnly=${unmatchedOnly}`
  );

/** Every message in one thread, newest first. */
export const getMessagingConversation = (
  threadId: string,
  pageNumber = 1,
  pageSize = 50
): Promise<PaginatedResponse<MessagingMessage>> =>
  get(
    `/api/messaging/conversations/${threadId}?pageNumber=${pageNumber}&pageSize=${pageSize}`
  );

/**
 * Every SMS with one contact, across every number and both channels — the
 * prospect's message timeline.
 */
export const getContactMessages = (
  contactType: MessagingContactType,
  contactId: string,
  pageNumber = 1,
  pageSize = 50
): Promise<PaginatedResponse<MessagingMessage>> =>
  get(
    `/api/messaging/contacts/${contactType}/${contactId}/messages` +
      `?pageNumber=${pageNumber}&pageSize=${pageSize}`
  );

export const markConversationRead = (threadId: string): Promise<void> =>
  post(`/api/messaging/conversations/${threadId}/mark-read`, {});

/**
 * Contacts sharing an unmatched thread's number. More than one is the sibling
 * case — show a picker rather than guessing.
 */
export const getThreadMatchCandidates = (
  threadId: string
): Promise<MessagingMatchCandidate[]> =>
  get(`/api/messaging/conversations/${threadId}/match-candidates`);

/** Attaches an unmatched thread to a player or guardian. */
export const linkConversation = (
  threadId: string,
  recipient: { contactId: string; contactType: MessagingContactType }
): Promise<void> =>
  post(`/api/messaging/conversations/${threadId}/link`, recipient);

// --- Phone numbers (admin) ---

export const getMessagingPhoneNumbers = (
  includeReleased = false
): Promise<MessagingPhoneNumber[]> =>
  get(`/api/messaging/phone-numbers?includeReleased=${includeReleased}`);

export const getMessagingPhoneNumber = (
  id: string
): Promise<MessagingPhoneNumber> => get(`/api/messaging/phone-numbers/${id}`);

/** Numbers the organization has, and how many it may claim without approval. */
export const getMessagingNumberQuota = (): Promise<MessagingNumberQuota> =>
  get("/api/messaging/phone-numbers/quota");

/**
 * Live Twilio inventory. Deliberately not cached anywhere: a number listed here can be
 * sold to someone else before it is claimed, and a stale list turns a search into a lie.
 */
export const searchAvailableNumbers = (params: {
  areaCode?: string;
  contains?: string;
  region?: string;
  limit?: number;
}): Promise<AvailableNumberSearch> => {
  const query = new URLSearchParams();
  if (params.areaCode) query.set("areaCode", params.areaCode);
  if (params.contains) query.set("contains", params.contains);
  if (params.region) query.set("region", params.region);
  if (params.limit) query.set("limit", String(params.limit));
  return get(`/api/messaging/phone-numbers/available?${query.toString()}`);
};

/**
 * Leases a number: RosterPilot buys it from Twilio, registers it for sending, and wires
 * its inbound webhook. Conflicts carry a `code` — `quota_exceeded`, `number_unavailable`,
 * or `number_taken` — because the caller's next move differs for each.
 */
export const claimPhoneNumber = (
  request: ClaimPhoneNumberRequest
): Promise<MessagingPhoneNumber> =>
  post("/api/messaging/phone-numbers/claim", request);

/**
 * Releases a number. It goes back to Twilio and the billing stops, permanently — Twilio may
 * resell it to anyone. The row and its threads are kept, because the conversation history
 * is the point of org ownership.
 */
export const releaseMessagingPhoneNumber = (id: string): Promise<void> =>
  del(`/api/messaging/phone-numbers/${id}`);

// --- Requests for numbers beyond the quota ---

export const getNumberRequests = (
  status?: string
): Promise<MessagingNumberRequest[]> =>
  get(
    `/api/messaging/phone-numbers/requests${status ? `?status=${status}` : ""}`
  );

/** One open request per organization at a time — a second returns 409. */
export const createNumberRequest = (
  request: CreateNumberRequestRequest
): Promise<MessagingNumberRequest> =>
  post("/api/messaging/phone-numbers/requests", request);

export const cancelNumberRequest = (id: string): Promise<void> =>
  del(`/api/messaging/phone-numbers/requests/${id}`);

// --- Platform admin (system_admin only) ---

/** The cross-organization queue, oldest first. */
export const getAdminNumberRequests = (
  status?: string
): Promise<MessagingNumberRequest[]> =>
  get(`/api/admin/messaging/number-requests${status ? `?status=${status}` : ""}`);

/** Same inventory search, without any one org's quota attached. */
export const searchAdminAvailableNumbers = (params: {
  areaCode?: string;
  contains?: string;
  region?: string;
}): Promise<AvailableNumber[]> => {
  const query = new URLSearchParams();
  if (params.areaCode) query.set("areaCode", params.areaCode);
  if (params.contains) query.set("contains", params.contains);
  if (params.region) query.set("region", params.region);
  return get(`/api/admin/messaging/available-numbers?${query.toString()}`);
};

/**
 * Buys the number for the requesting organization, over its quota. A failed approval leaves
 * the request pending, so the admin can pick a different number.
 */
export const approveNumberRequest = (
  id: string,
  request: ApproveNumberRequestRequest
): Promise<MessagingNumberRequest> =>
  post(`/api/admin/messaging/number-requests/${id}/approve`, request);

/** The note is required and is shown back to the organization. */
export const denyNumberRequest = (
  id: string,
  note: string
): Promise<MessagingNumberRequest> =>
  post(`/api/admin/messaging/number-requests/${id}/deny`, { note });

/** Every leased number across every organization, with what it costs. */
export const getAdminLeasedNumbers = (
  includeReleased = false
): Promise<AdminLeasedNumbers> =>
  get(`/api/admin/messaging/numbers?includeReleased=${includeReleased}`);

/** Null restores the platform default. Lowering it never takes a number away. */
export const setOrganizationMessagingQuota = (
  organizationId: string,
  quota: number | null
): Promise<MessagingNumberQuota> =>
  patch(`/api/admin/messaging/organizations/${organizationId}/quota`, { quota });

/** Full grant history for a number, including revoked grants. */
export const getPhoneNumberAssignments = (
  id: string
): Promise<MessagingNumberHolder[]> =>
  get(`/api/messaging/phone-numbers/${id}/assignments`);

export const assignPhoneNumber = (id: string, userId: string): Promise<void> =>
  post(`/api/messaging/phone-numbers/${id}/assignments`, { userId });

/**
 * Ends a coach's grant. The number stays with the organization and the next
 * holder inherits every thread on it.
 */
export const revokePhoneNumber = (id: string, userId: string): Promise<void> =>
  del(`/api/messaging/phone-numbers/${id}/assignments/${userId}`);

// --- Templates ---

export const getMergeFields = (): Promise<MergeField[]> =>
  get("/api/messaging/templates/merge-fields");

export const getMessageTemplates = (): Promise<MessagingTemplate[]> =>
  get("/api/messaging/templates");

export const getMessageTemplate = (id: string): Promise<MessagingTemplate> =>
  get(`/api/messaging/templates/${id}`);

export const createMessageTemplate = (
  request: SaveMessagingTemplateRequest
): Promise<MessagingTemplate> => post("/api/messaging/templates", request);

export const updateMessageTemplate = (
  id: string,
  request: SaveMessagingTemplateRequest
): Promise<MessagingTemplate> => put(`/api/messaging/templates/${id}`, request);

export const deleteMessageTemplate = (id: string): Promise<void> =>
  del(`/api/messaging/templates/${id}`);

// --- Sending ---

/** The coach's own phone plus every org number assigned to them, as one picker. */
export const getMessageSenders = (): Promise<MessageSender[]> =>
  get("/api/messaging/senders");

/**
 * Textable contacts — every player, plus a guardian entry for each player with a
 * parent phone on file.
 */
export const getMessagingRecipients = (
  search?: string,
  take = 100,
  teamId?: string,
  tagFilters?: Record<string, string[]>
): Promise<MessagingContactSummary[]> => {
  const query = new URLSearchParams({ take: String(take) });
  if (search?.trim()) query.set("search", search.trim());
  if (teamId) query.set("teamId", teamId);

  // Same tag_* encoding the roster page uses — spaces stripped, repeated per value — so the
  // server resolves both through one parser and the two lists can never disagree about who a
  // tag selects.
  if (tagFilters) {
    for (const [tagName, values] of Object.entries(tagFilters)) {
      if (!values?.length) continue;
      const param = `tag_${tagName.replace(/\s+/g, "")}`;
      for (const value of values) query.append(param, value);
    }
  }

  return get(`/api/messaging/recipients?${query.toString()}`);
};

/**
 * Resolves the body against every recipient without sending, so the counts and
 * every blocker are visible before anything goes out.
 */
export const previewBulkSend = (
  request: PreviewBulkSendRequest
): Promise<BulkSendPreview> => post("/api/messaging/preview", request);

/** Sends to exactly one recipient. Bulk is this called once per preview entry. */
export const sendMessage = (
  request: SendMessageRequest
): Promise<SendMessageResponse> => post("/api/messaging/send", request);

/**
 * Records a personal-phone send after the native composer was handed the body.
 * Logged as attempted only — the composer closing is not a delivery receipt.
 */
export const logPersonalSend = (
  request: LogPersonalSendRequest
): Promise<SendMessageResponse> =>
  post("/api/messaging/personal-sends", request);

// --- Opt-outs ---

export const getOptOuts = (): Promise<MessagingOptOut[]> =>
  get("/api/messaging/opt-outs");

/** Records an opt-out given by some other means (a phone call, an email). */
export const createOptOut = (phoneNumber: string): Promise<void> =>
  post("/api/messaging/opt-outs", { phoneNumber });

export const deleteOptOut = (normalizedPhone: string): Promise<void> =>
  del(`/api/messaging/opt-outs/${normalizedPhone}`);
